/**
 * Google Tag Manager Loader Utility
 * 
 * Loads GTM dynamically and non-blocking
 * Ensures GTM loads only once
 * Fails silently if disabled or ID missing
 */

// Global flag to prevent multiple loads
let gtmLoaded = false;
let gtmLoadInProgress = false;

/**
 * Initialize Google Tag Manager
 * @param {string} containerId - GTM Container ID (e.g., GTM-XXXXXXX)
 * @returns {boolean} - Returns true if GTM was initialized, false otherwise
 */
export const loadGTM = (containerId) => {
  // Validate container ID format
  if (!containerId || typeof containerId !== 'string') {
    return false;
  }

  // Validate format (GTM-XXXXXXX)
  const gtmIdPattern = /^GTM-[A-Z0-9]+$/i;
  if (!gtmIdPattern.test(containerId.trim())) {
    return false;
  }

  const id = containerId.trim();

  // Prevent multiple loads
  if (gtmLoaded || gtmLoadInProgress) {
    return gtmLoaded;
  }

  // Disable in development mode
  if (process.env.NODE_ENV === 'development') {
    return false;
  }

  // Ensure we're in browser environment
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }

  gtmLoadInProgress = true;

  try {
    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    
    // GTM initialization function
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });

    // Create and inject GTM script (head)
    const gtmScript = document.createElement('script');
    gtmScript.async = true;
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
    gtmScript.onload = () => {
      gtmLoaded = true;
      gtmLoadInProgress = false;
    };
    gtmScript.onerror = () => {
      gtmLoadInProgress = false;
      // Fail silently - no console error
    };
    
    // Insert script in head (deferred, non-blocking)
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(gtmScript, firstScript);
    } else {
      document.head.appendChild(gtmScript);
    }

    // Create and inject noscript iframe (body)
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${id}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    
    // Insert noscript in body
    if (document.body) {
      document.body.insertBefore(noscript, document.body.firstChild);
    } else {
      // Wait for body to be available
      const observer = new MutationObserver((mutations, obs) => {
        if (document.body) {
          document.body.insertBefore(noscript, document.body.firstChild);
          obs.disconnect();
        }
      });
      observer.observe(document.documentElement, { childList: true });
    }

    return true;
  } catch (error) {
    // Fail silently - no console error
    gtmLoadInProgress = false;
    return false;
  }
};

/**
 * Push event to GTM dataLayer
 * @param {Object} data - Data to push to dataLayer
 */
export const pushToDataLayer = (data) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    try {
      window.dataLayer.push(data);
    } catch (error) {
      // Fail silently
    }
  }
};

/**
 * Initialize GTM with delay (non-blocking)
 * Uses requestIdleCallback if available, otherwise setTimeout
 * @param {string} containerId - GTM Container ID
 */
export const initGTMDeferred = (containerId) => {
  if (!containerId) {
    return;
  }

  const load = () => {
    loadGTM(containerId);
  };

  // Use requestIdleCallback for better performance, fallback to setTimeout
  if (typeof window !== 'undefined' && window.requestIdleCallback) {
    window.requestIdleCallback(load, { timeout: 2000 });
  } else {
    // Fallback: load after initial render (non-blocking)
    setTimeout(load, 100);
  }
};
